import Dexie, { type Table } from "dexie";

const STORAGE_TABLE = "records";

type StoredRecord<T> = {
  namespace: string;
  id: string;
  value: T;
};

class MacroTrackerDb extends Dexie {
  records!: Table<StoredRecord<unknown>, [string, string]>;

  constructor(name: string) {
    super(name);
    this.version(1).stores({
      records: "&[namespace+id], namespace, id",
    });
    // v2 added the sync-records table (originally registered in syncStorage.ts)
    this.version(2).stores({
      "sync-records": "&id, namespace, entityId, operation",
    });
    // v3 was an emergency recovery migration that some existing databases passed
    // through — re-declare both tables so those DBs can open cleanly
    this.version(3).stores({
      records: "&[namespace+id], namespace, id",
      "sync-records": "&id, namespace, entityId, operation",
    });
    // v4: document-model migration. Add the `orphaned-legacy-records` sidecar
    // table for rows the client-side doc migration couldn't map cleanly.
    // We intentionally do NOT delete legacy namespaces here — docModelMigration
    // reads them first to build DiaryDay / Recipe docs, then cleans up after
    // a successful run (guarded by a localStorage flag).
    this.version(4).stores({
      records: "&[namespace+id], namespace, id",
      "sync-records": "&id, namespace, entityId, operation",
      "orphaned-legacy-records": "++autoId, namespace, originalId, recordedAt",
    });
    this.records = this.table(STORAGE_TABLE);
  }
}

// ---------------------------------------------------------------------------
// Per-user DB registry
// ---------------------------------------------------------------------------

const dbRegistry = new Map<string, MacroTrackerDb>();
let activeUserId: string | null = null;

/**
 * Call this once the authenticated user's ID is known (router guard, after
 * ensureValidSession). All subsequent getActiveDb() calls resolve to the
 * correct user-scoped database.
 */
export const initUserDb = (userId: string): void => {
  activeUserId = userId;
  const dbName = `macrotracker:user:${userId}`;
  if (!dbRegistry.has(dbName)) {
    dbRegistry.set(dbName, new MacroTrackerDb(dbName));
  }
};

/**
 * Returns the active user's Dexie instance. Throws if initUserDb has not
 * been called yet — every service operation is async and happens after auth,
 * so this should never be reached in production.
 */
export const getActiveDb = (): MacroTrackerDb => {
  if (!activeUserId) {
    throw new Error(
      "[storage] No active user — call initUserDb(userId) before accessing the database.",
    );
  }
  const dbName = `macrotracker:user:${activeUserId}`;
  const instance = dbRegistry.get(dbName);
  if (!instance) {
    throw new Error(`[storage] DB instance not found for user (dbName=${dbName}).`);
  }
  return instance;
};

/** Call on sign-out to deactivate the current user's DB context. */
export const clearActiveUser = (): void => {
  activeUserId = null;
};

// ---------------------------------------------------------------------------
// Per-user in-memory fallback (when IndexedDB is unavailable, e.g. unit tests)
// ---------------------------------------------------------------------------

const memoryStores = new Map<string, Map<string, Map<string, unknown>>>();

const hasIndexedDb = (): boolean => typeof indexedDB !== "undefined";

const getMemoryNamespace = <T>(namespace: string): Map<string, T> => {
  const key = activeUserId ?? "__no-user__";
  if (!memoryStores.has(key)) {
    memoryStores.set(key, new Map());
  }
  const userStore = memoryStores.get(key)!;
  if (!userStore.has(namespace)) {
    userStore.set(namespace, new Map());
  }
  return userStore.get(namespace) as Map<string, T>;
};

// ---------------------------------------------------------------------------
// Public types & factory
// ---------------------------------------------------------------------------

export type StorageEntity = {
  id: string;
};

export type StorageService<T extends StorageEntity> = {
  getAll: () => Promise<T[]>;
  getById: (id: string) => Promise<T | null>;
  save: (entity: T) => Promise<T>;
  update: (id: string, updates: Partial<T>) => Promise<T | null>;
  remove: (id: string) => Promise<boolean>;
  clear: () => Promise<void>;
};

export const createStorageService = <T extends StorageEntity>(
  namespace: string,
): StorageService<T> => ({
  getAll: async () => {
    if (!hasIndexedDb()) {
      return Array.from(getMemoryNamespace<T>(namespace).values());
    }
    const db = getActiveDb();
    const rows = await db.records.where("namespace").equals(namespace).toArray();
    return rows.map((row) => row.value as T);
  },
  getById: async (id) => {
    if (!hasIndexedDb()) {
      return getMemoryNamespace<T>(namespace).get(id) ?? null;
    }
    const db = getActiveDb();
    const record = await db.records.get([namespace, id]);
    return (record?.value as T | undefined) ?? null;
  },
  save: async (entity) => {
    if (!hasIndexedDb()) {
      getMemoryNamespace<T>(namespace).set(entity.id, entity);
      return entity;
    }
    const db = getActiveDb();
    await db.records.put({ namespace, id: entity.id, value: entity });
    return entity;
  },
  update: async (id, updates) => {
    if (!hasIndexedDb()) {
      const store = getMemoryNamespace<T>(namespace);
      const current = store.get(id);
      if (!current) return null;
      const updated = { ...current, ...updates } as T;
      store.set(id, updated);
      return updated;
    }
    const db = getActiveDb();
    const record = await db.records.get([namespace, id]);
    if (!record) return null;
    const updated = { ...(record.value as T), ...updates } as T;
    await db.records.put({ namespace, id, value: updated });
    return updated;
  },
  remove: async (id) => {
    if (!hasIndexedDb()) {
      return getMemoryNamespace<T>(namespace).delete(id);
    }
    const db = getActiveDb();
    const record = await db.records.get([namespace, id]);
    if (!record) return false;
    await db.records.delete([namespace, id]);
    return true;
  },
  clear: async () => {
    if (!hasIndexedDb()) {
      getMemoryNamespace<T>(namespace).clear();
      return;
    }
    const db = getActiveDb();
    await db.records.where("namespace").equals(namespace).delete();
  },
});
